/**
 * Permission approval modal for module installation
 */
import { useState, useMemo } from 'react';
import type { ModulePermission, PermissionCheckResult, LoadedModule } from '@amorphous/core';
import { PERMISSION_DESCRIPTIONS, PERMISSION_CATEGORIES } from '@amorphous/core';
import './PermissionModal.css';

interface PermissionModalProps {
  module: LoadedModule;
  permissionCheck: PermissionCheckResult;
  onApprove: (permissions: ModulePermission[]) => void;
  onDeny: () => void;
}

export function PermissionModal({
  module,
  permissionCheck,
  onApprove,
  onDeny,
}: PermissionModalProps) {
  const { manifest } = module;

  // Track which permissions are selected (all requested by default)
  const [selected, setSelected] = useState<Set<ModulePermission>>(
    new Set(manifest.permissions)
  );

  // Categorize permissions
  const categorized = useMemo(() => {
    const safe: ModulePermission[] = [];
    const prompt: ModulePermission[] = [];
    const dangerous: ModulePermission[] = [];

    for (const perm of manifest.permissions) {
      const category = PERMISSION_CATEGORIES[perm];
      if (category === 'safe') safe.push(perm);
      else if (category === 'prompt') prompt.push(perm);
      else if (category === 'dangerous') dangerous.push(perm);
    }

    return { safe, prompt, dangerous };
  }, [manifest.permissions]);

  const hasDangerous = categorized.dangerous.length > 0;

  const togglePermission = (perm: ModulePermission) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(perm)) {
        next.delete(perm);
      } else {
        next.add(perm);
      }
      return next;
    });
  };

  const handleApprove = () => {
    onApprove(Array.from(selected));
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onDeny();
    }
  };

  return (
    <div className="permission-modal-overlay" onClick={handleOverlayClick}>
      <div className="permission-modal">
        <div className="permission-modal-header">
          <h2>Install Module</h2>
          <button className="close-btn" onClick={onDeny}>×</button>
        </div>

        <div className="permission-modal-body">
          {/* Module Info */}
          <div className="module-info">
            <div className="module-name">{manifest.name}</div>
            <div className="module-meta">
              <span className="module-id">{manifest.id}</span>
              <span className="module-version">v{manifest.version}</span>
            </div>
            <div className="module-description">{manifest.description}</div>
            <div className="module-author">
              By: <span className="author-key">{manifest.author.alias || manifest.author.pubkey.slice(0, 12)}...</span>
            </div>
            {module.trustedSigners.length > 0 && (
              <div className="module-signers">
                Signed by {module.trustedSigners.length} trusted key(s)
              </div>
            )}
          </div>

          {/* Permission Sections */}
          <div className="permissions-container">
            <h3>Requested Permissions</h3>

            {/* Safe Permissions */}
            {categorized.safe.length > 0 && (
              <div className="permission-section safe">
                <div className="section-header">
                  <span className="section-icon">✓</span>
                  <span className="section-title">Safe (Auto-granted)</span>
                </div>
                <ul className="permission-list">
                  {categorized.safe.map(perm => (
                    <li key={perm} className="permission-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selected.has(perm)}
                          onChange={() => togglePermission(perm)}
                        />
                        <span className="permission-name">{perm}</span>
                        <span className="permission-desc">
                          {PERMISSION_DESCRIPTIONS[perm] || 'No description'}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Prompt Permissions */}
            {categorized.prompt.length > 0 && (
              <div className="permission-section prompt">
                <div className="section-header">
                  <span className="section-icon">!</span>
                  <span className="section-title">Requires Approval</span>
                </div>
                <ul className="permission-list">
                  {categorized.prompt.map(perm => (
                    <li key={perm} className="permission-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selected.has(perm)}
                          onChange={() => togglePermission(perm)}
                        />
                        <span className="permission-name">{perm}</span>
                        <span className="permission-desc">
                          {PERMISSION_DESCRIPTIONS[perm] || 'No description'}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Dangerous Permissions */}
            {categorized.dangerous.length > 0 && (
              <div className="permission-section dangerous">
                <div className="section-header">
                  <span className="section-icon">⚠</span>
                  <span className="section-title">Dangerous</span>
                </div>
                <div className="danger-warning">
                  These permissions can significantly affect your data and privacy.
                  Only grant if you fully trust this module.
                </div>
                <ul className="permission-list">
                  {categorized.dangerous.map(perm => (
                    <li key={perm} className="permission-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selected.has(perm)}
                          onChange={() => togglePermission(perm)}
                        />
                        <span className="permission-name">{perm}</span>
                        <span className="permission-desc">
                          {PERMISSION_DESCRIPTIONS[perm] || 'No description'}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Unknown Permissions */}
            {permissionCheck.unknown.length > 0 && (
              <div className="permission-section unknown">
                <div className="section-header">
                  <span className="section-icon">?</span>
                  <span className="section-title">Unknown Permissions</span>
                </div>
                <div className="unknown-warning">
                  These permissions are not recognized and cannot be granted.
                </div>
                <ul className="permission-list disabled">
                  {permissionCheck.unknown.map(perm => (
                    <li key={perm} className="permission-item">
                      <span className="permission-name">{perm}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="permission-modal-footer">
          <button className="btn-deny" onClick={onDeny}>
            Cancel
          </button>
          <button
            className={`btn-approve ${hasDangerous ? 'has-dangerous' : ''}`}
            onClick={handleApprove}
            disabled={selected.size === 0}
          >
            {hasDangerous ? 'Install Anyway' : 'Install'}
          </button>
        </div>
      </div>
    </div>
  );
}
